import RestoreSubjectType from '@Application/SubjectTypeDefinitions/Commands/RestoreSubjectType';
import restoreSubjectType from '@Application/SubjectTypeDefinitions/restoreSubjectType';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import ArchiveSubjectTypeDialog from '@UI/Pages/Administration/SubjectTypes/Shared/ConfirmationDialogs/ArchiveSubjectTypeDialog';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type SubjectTypeDefinitionView from '@UI/Store/SubjectTypeDefinitions/Views/SubjectTypeDefinitionView';
import { css } from '@emotion/react';
import { Archive, ArchiveRestore, Pencil } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from '@verclam/synapse';
import { Button } from '@verclam/ui-kit';

const actionsStyle = css`
	display: flex;
	justify-content: flex-end;
	gap: 0.5rem;
`;

type Props = {
	readonly subjectType: SubjectTypeDefinitionView;
};

/** Leaf actions: an active type is edited or archived (after confirmation), an archived one restored. */
const SubjectTypeCardActions: React.FC<Props> = ({ subjectType }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { user } = useStrictAuthenticatedUser();
	const accountId = user.account?.id ?? null;
	const [isArchiveVisible, setIsArchiveVisible] = React.useState(false);

	const handleEditClick = React.useCallback(() => {
		navigate(`/administration/subject-types/${subjectType.id}`);
	}, [navigate, subjectType.id]);

	const handleArchiveClick = React.useCallback(() => {
		setIsArchiveVisible(true);
	}, []);

	const handleArchiveHide = React.useCallback(() => {
		setIsArchiveVisible(false);
	}, []);

	const handleRestoreClick = React.useCallback(() => {
		if (accountId === null) {
			return;
		}

		dispatch(restoreSubjectType(new RestoreSubjectType(accountId, subjectType.id))).catch(
			ignoreHandledError,
		);
	}, [accountId, dispatch, subjectType.id]);

	if (subjectType.isArchived) {
		return (
			<div css={actionsStyle}>
				<Button
					type="button"
					text
					icon={<ArchiveRestore size={16} />}
					label="Restaurer"
					onClick={handleRestoreClick}
				/>
			</div>
		);
	}

	return (
		<div css={actionsStyle}>
			<Button
				type="button"
				text
				icon={<Pencil size={16} />}
				label="Modifier"
				onClick={handleEditClick}
			/>
			<Button
				type="button"
				severity="danger"
				outlined
				icon={<Archive size={16} />}
				label="Archiver"
				onClick={handleArchiveClick}
			/>
			<ArchiveSubjectTypeDialog
				subjectType={subjectType}
				visible={isArchiveVisible}
				onHide={handleArchiveHide}
			/>
		</div>
	);
};

export default SubjectTypeCardActions;
